import {
  pasteBlocker,
  readAskAnswer,
  pasteKind,
  plaudShareUrl,
  readSharePage,
  segmentsFromPastedText,
  type SharedRecording,
  transcriptText,
} from "@roof-hawk/core";

import { supabase, unwrap } from "./supabase";

/** A recorded conversation, as the job screen needs it. */
export interface Conversation {
  id: string;
  job_id: string | null;
  rep_id: string | null;
  name: string | null;
  started_at: string;
  duration_ms: number | null;
  transcript_text: string | null;
  plaud_summary_md: string | null;
  plaud_share_url: string | null;
  source: string;
  status: string;
  match_method: string | null;
  reviewed_at: string | null;
  created_at: string;
}

const COLUMNS =
  "id,job_id,rep_id,name,started_at,duration_ms,transcript_text," +
  "plaud_summary_md,plaud_share_url,source,status,match_method,reviewed_at,created_at";

/** Every recording on a job, newest first. */
export async function fetchConversations(
  jobId: string,
): Promise<Conversation[]> {
  return unwrap<Conversation[]>(
    await supabase
      .from("conversations")
      .select(COLUMNS)
      .eq("job_id", jobId)
      .order("started_at", { ascending: false }),
  );
}

/**
 * Ask the server to fetch a share page for us.
 *
 * The browser cannot fetch plaud.ai itself, so `plaud-fetch` does it. What
 * comes back is the page exactly as it arrived; the reading of it is core's
 * job, here in the browser, so there is one copy of that logic rather than two.
 *
 * `functions.invoke` reports a non-2xx as a generic error and hides the body,
 * and the body is where the sentence a rep can act on lives — "the link may be
 * invite-only", not "non-2xx status code".
 */
async function fetchSharePage(url: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke("plaud-fetch", {
    body: { url },
  });
  if (!error) return (data as { html: string }).html;

  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    const payload = (await context.json().catch(() => null)) as {
      error?: string;
    } | null;
    if (payload && typeof payload.error === "string") {
      throw new Error(payload.error);
    }
  }
  throw new Error(error.message);
}

/** What a successful import produced, for the screen to say so. */
export interface ImportedConversation {
  conversation: Conversation;
  /** True when the words came from a link rather than being pasted. */
  fromLink: boolean;
  /** True when the page gave a transcript but no summary, or the other way. */
  partial: boolean;
}

/**
 * Turn what a rep pasted into a conversation on this job.
 *
 * One box takes both a share link and the transcript itself, because "is this
 * a URL" is a question a computer can answer and one nobody should be asked.
 *
 * A recording that arrives this way is `matched` and `manual` from the moment
 * it lands: a person chose the job by being on it, which outranks any scoring
 * (ADR 0064), so it is stamped reviewed rather than arriving as a guess.
 */
export async function importConversation(options: {
  jobId: string;
  repId: string | null;
  paste: string;
}): Promise<ImportedConversation> {
  const blocker = pasteBlocker(options.paste);
  if (blocker) throw new Error(blocker);

  const kind = pasteKind(options.paste);
  const url = kind === "link" ? plaudShareUrl(options.paste) : null;

  let read: SharedRecording | null = null;
  if (url) {
    read = readSharePage(await fetchSharePage(url));
    if (!read) {
      throw new Error(
        "That link opened, but there was no transcript or summary on it. " +
          "Either the share was made without them ticked, or it is " +
          "invite-only. Re-share it as a public link, or paste the " +
          "transcript itself into this box.",
      );
    }
  } else {
    const segments = segmentsFromPastedText(options.paste);
    read = {
      name: null,
      startedAt: null,
      durationMs: null,
      summaryMd: null,
      transcriptText: transcriptText(segments),
      segments,
    };
  }

  const row = {
    // Nothing from Plaud identifies a pasted recording, so the link does when
    // there is one and the row's own id does when there is not. It has to be
    // unique and it never has to mean anything.
    plaud_file_id: url ?? `pasted:${crypto.randomUUID()}`,
    plaud_share_url: url,
    job_id: options.jobId,
    rep_id: options.repId,
    name: read.name,
    // A share page need not say when it was recorded. Today is a worse answer
    // than no answer, but the column is what the list is ordered by, so it has
    // to be something — and "when somebody filed it" is at least true.
    started_at: read.startedAt ?? new Date().toISOString(),
    duration_ms: read.durationMs,
    transcript: read.segments,
    transcript_text: read.transcriptText,
    plaud_summary_md: read.summaryMd,
    source: url ? "plaud_link" : "pasted",
    status: "matched",
    match_method: "manual",
    match_confidence: null,
    reviewed_by: options.repId,
    reviewed_at: new Date().toISOString(),
  };

  const { data, error } = await supabase
    .from("conversations")
    .insert(row)
    .select(COLUMNS)
    .single();

  if (error) {
    throw new Error(
      error.code === "23505"
        ? "That recording is already on a job in Roof Board."
        : error.message,
    );
  }

  return {
    conversation: data as unknown as Conversation,
    fromLink: url !== null,
    partial: !read.summaryMd || !read.transcriptText,
  };
}

/* ---------------------------------------------- asking the transcripts --- */

/** A question somebody asked of this job's transcripts, and what came back. */
export interface ConversationQuestion {
  id: string;
  question: string;
  answer: string;
  conversation_id: string | null;
  created_at: string;
  asker?: { full_name: string | null } | null;
}

/** The question history on a job, newest first. */
export async function fetchConversationQuestions(
  jobId: string,
): Promise<ConversationQuestion[]> {
  return unwrap<ConversationQuestion[]>(
    await supabase
      .from("conversation_questions")
      .select(
        "id,question,answer,conversation_id,created_at," +
          "asker:profiles!conversation_questions_asked_by_fkey(full_name)",
      )
      .eq("job_id", jobId)
      .order("created_at", { ascending: false })
      .limit(50),
  );
}

/**
 * Ask this job's transcripts a question.
 *
 * The transcripts are not sent up — `conversation-ask` reads them itself, as
 * the caller, so RLS answers "may they see that job" the way it does
 * everywhere else (ADR 0113).
 *
 * `functions.invoke` reports a non-2xx as a generic error and hides the body,
 * and the body is where the sentence a rep can act on lives — "there is no
 * transcript on this job yet", not "non-2xx status code". Same unwrapping as
 * `fetchSharePage` above, for the same reason.
 */
export async function askConversations(options: {
  jobId: string;
  question: string;
  conversationId?: string | null;
}): Promise<string> {
  const { data, error } = await supabase.functions.invoke("conversation-ask", {
    body: {
      jobId: options.jobId,
      question: options.question,
      conversationId: options.conversationId ?? null,
    },
  });

  if (!error) return readAskAnswer((data as { answer?: unknown }).answer);

  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    const payload = (await context.json().catch(() => null)) as {
      error?: string;
    } | null;
    if (payload && typeof payload.error === "string") {
      throw new Error(payload.error);
    }
  }
  throw new Error(
    error.message ||
      "Could not reach the AI. It may not be deployed yet — Actions → Deploy to production, with 'conversation-ask' in the functions box.",
  );
}

/**
 * The text of a chosen file.
 *
 * `Blob.text()` where there is one, and `FileReader` where there is not. The
 * fallback is not hypothetical twice over: iOS Safari only grew `Blob.text`
 * in 14, and this app is installed on phones — and jsdom has no `Blob.text`
 * at all, so without it the upload path could not be tested.
 */
export async function readFileText(file: Blob): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new Error("That file could not be read. Try pasting it instead."));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file);
  });
}
